import React, { useContext, useEffect, useState, useCallback } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { showToast } from '@/lib/custom-toast';
import { Zap, CheckCircle2, Sparkles, ShoppingCart, ArrowLeft } from 'lucide-react';
import { formatCurrency } from '@/utils/currencyFormat';

const VoiceCredits = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [voiceUsage, setVoiceUsage] = useState(null);

  const sessionId = searchParams.get('session_id');
  const success = searchParams.get('success');

  const fetchPackages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/voice/packages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPackages(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        showToast.error('Necesitas una suscripción Premium o Pro para comprar créditos Voice AI');
        setTimeout(() => navigate('/pricing'), 2000);
      } else {
        showToast.error('Error al cargar paquetes');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  const fetchVoiceUsage = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/voice/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVoiceUsage(response.data);
    } catch (error) {
      console.error('Error fetching voice usage:', error);
    }
  }, [token]);

  useEffect(() => {
    if (success === 'true' && sessionId) {
      showToast.success('¡Compra exitosa! Tus créditos han sido agregados');
      navigate('/voice-credits', { replace: true });
    }

    fetchPackages();
    fetchVoiceUsage();
  }, [success, sessionId, navigate, fetchPackages, fetchVoiceUsage]);



  const handlePurchase = async (packageId) => {
    setPurchasing(packageId);
    try {
      const response = await axios.post(
        `${API}/voice/purchase`,
        { package_id: packageId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al procesar compra');
      setPurchasing(null);
    }
  };


  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-lg text-slate-600">Cargando paquetes...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-8 pb-12">
        {/* Header */}
        <div className="text-center space-y-4">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Button>

          <div className="flex items-center justify-center gap-3">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Zap className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900">Créditos Voice AI</h1>
          </div>

          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Compra créditos adicionales para seguir usando Voice AI más allá del límite mensual
          </p>

          {/* Current Usage */}
          {voiceUsage && (
            <Card className="max-w-md mx-auto p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
              <div className="text-center">
                <p className="text-sm text-purple-700 font-medium mb-2">Tu Saldo Actual</p>
                <div className="flex items-center justify-center gap-4">
                  <div>
                    <p className="text-xs text-slate-600">Uso Mensual</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {voiceUsage.usage_count}/{voiceUsage.monthly_limit}
                    </p>
                  </div>
                  <div className="w-px h-12 bg-purple-200"></div>
                  <div>
                    <p className="text-xs text-slate-600">Créditos Extra</p>
                    <p className="text-2xl font-bold text-purple-900">
                      +{voiceUsage.purchased_credits || 0}
                    </p>
                  </div>
                  <div className="w-px h-12 bg-purple-200"></div>
                  <div>
                    <p className="text-xs text-slate-600">Total Restante</p>
                    <p className="text-2xl font-bold text-green-600">
                      {voiceUsage.remaining}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Packages */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packages.map((pkg) => {
            const pricePerCredit = (pkg.price_mxn / pkg.credits).toFixed(2);

            return (
              <Card
                key={pkg.id}
                className={`relative overflow-hidden transition-all hover:shadow-xl ${pkg.is_popular
                    ? 'border-2 border-purple-400 shadow-lg scale-105'
                    : 'border-slate-200'
                  }`}
              >
                {/* Popular Badge */}
                {pkg.is_popular && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-bold px-4 py-1 rounded-bl-lg">
                    ⭐ MÁS POPULAR
                  </div>
                )}

                <div className="p-6 space-y-6">
                  {/* Header */}
                  <div className="text-center space-y-2">
                    <div className="inline-flex p-3 bg-purple-100 rounded-xl">
                      <Zap className="w-8 h-8 text-purple-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900">{pkg.name}</h3>
                    {pkg.description && (
                      <p className="text-sm text-slate-600">{pkg.description}</p>
                    )}
                  </div>

                  {/* Price */}
                  <div className="text-center py-4 border-y border-slate-200">
                    <p className="text-4xl font-bold text-purple-600">
                      {formatCurrency(pkg.price_mxn)}
                    </p>
                    <p className="text-sm text-slate-500 mt-2">
                      ${pricePerCredit} MXN por crédito
                    </p>
                    {pkg.discount_percentage > 0 && (
                      <span className="inline-block mt-2 px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                        {pkg.discount_percentage}% de descuento
                      </span>
                    )}
                  </div>

                  {/* Features */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-sm">
                        <span className="font-bold text-purple-600">{pkg.credits}</span> transacciones de voz
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-sm">Válidos por 1 mes desde la compra</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-sm">Clasificación automática con IA</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-sm">Sin límite de uso diario</span>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <Button
                    onClick={() => handlePurchase(pkg.id)}
                    disabled={purchasing !== null}
                    className={`w-full ${pkg.is_popular
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                        : 'bg-purple-600 hover:bg-purple-700'
                      }`}
                    size="lg"
                  >
                    {purchasing === pkg.id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Procesando...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        Comprar Ahora
                      </>
                    )}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Info Section */}
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-start gap-4">
            <Sparkles className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div className="space-y-2">
              <h3 className="font-bold text-slate-900 text-lg">¿Por qué comprar créditos Voice AI?</h3>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>Registra transacciones 10x más rápido usando solo tu voz</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>Clasificación automática con IA - detecta tipo, monto y categoría</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>Perfecto para cuando estás en movimiento o tienes muchas transacciones</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>Los créditos se suman a tu límite mensual base (100 Premium / 300 Pro)</span>
                </li>
              </ul>
            </div>
          </div>
        </Card>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto space-y-4">
          <h3 className="text-2xl font-bold text-slate-900 text-center mb-6">Preguntas Frecuentes</h3>

          <Card className="p-4">
            <h4 className="font-semibold text-slate-900 mb-2">¿Los créditos expiran?</h4>
            <p className="text-sm text-slate-600">
              Los créditos comprados expiran al final del mes siguiente a tu compra. Por ejemplo, si compras en enero,
              los créditos estarán disponibles hasta finales de febrero.
            </p>
          </Card>

          <Card className="p-4">
            <h4 className="font-semibold text-slate-900 mb-2">¿Se suman a mi límite mensual?</h4>
            <p className="text-sm text-slate-600">
              Sí. Si eres Premium (100/mes) y compras 50 créditos, tendrás 150 transacciones disponibles ese mes.
              Si eres Pro (300/mes) y compras 100 créditos, tendrás 400 transacciones disponibles.
            </p>
          </Card>

          <Card className="p-4">
            <h4 className="font-semibold text-slate-900 mb-2">¿Puedo comprar múltiples paquetes?</h4>
            <p className="text-sm text-slate-600">
              Sí, puedes comprar los paquetes que necesites y los créditos se acumularán.
            </p>
          </Card>

          <Card className="p-4">
            <h4 className="font-semibold text-slate-900 mb-2">¿Qué pasa si no tengo suscripción?</h4>
            <p className="text-sm text-slate-600">
              Los créditos Voice AI solo están disponibles para usuarios Premium y Pro.
              Si necesitas acceso, <a href="/pricing" className="text-purple-600 hover:underline font-semibold">actualiza tu suscripción aquí</a>.
            </p>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default VoiceCredits;
